'use client';

import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { classnames } from '@/utils/classnames/classnames';

interface ListMenuProps {
  onRename: () => void;
  onDelete: () => void;
  onSetDefault?: () => void;
  isDefault?: boolean;
}

const ListMenu = ({ onRename, onDelete, onSetDefault, isDefault }: ListMenuProps) => {
  const translate = useTranslations('common');
  const translateDashboard = useTranslations('dashboard');

  return (
    <Menu as="div" className="relative">
      <MenuButton
        aria-label={translate('actions')}
        className="text-primary inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white transition-colors hover:bg-gray-50"
      >
        <EllipsisHorizontalIcon aria-hidden className="h-5 w-5" />
      </MenuButton>

      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <MenuItems
          // EWM-771 — non-modal dropdown; skip headlessui's default body-scroll lock so
          // opening the menu doesn't hide the page scrollbar and shift content sideways.
          modal={false}
          className="absolute right-0 z-20 mt-1 w-56 origin-top-right overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none"
        >
          <MenuItem>
            {({ focus }) => (
              <button
                type="button"
                onClick={onRename}
                className={classnames('text-13 block w-full px-4 py-2.5 text-left text-gray-700', {
                  'bg-gray-50': focus,
                })}
              >
                {translate('rename')}
              </button>
            )}
          </MenuItem>
          {onSetDefault && !isDefault && (
            <MenuItem>
              {({ focus }) => (
                <button
                  type="button"
                  onClick={onSetDefault}
                  className={classnames(
                    'text-13 block w-full border-t border-gray-100 px-4 py-2.5 text-left text-gray-700',
                    { 'bg-gray-50': focus },
                  )}
                >
                  {translateDashboard('purchase-list-set-default')}
                </button>
              )}
            </MenuItem>
          )}
          <MenuItem>
            {({ focus }) => (
              <button
                type="button"
                onClick={onDelete}
                className={classnames(
                  'text-13 block w-full border-t border-gray-100 px-4 py-2.5 text-left text-red-600',
                  { 'bg-red-50': focus },
                )}
              >
                {translate('delete')}
              </button>
            )}
          </MenuItem>
        </MenuItems>
      </Transition>
    </Menu>
  );
};

export default ListMenu;
